import { useLoginStore } from "@/stores/loginStore";
import router from "@/routes/routes";
import { Notification } from "element-ui";
import { getPending, getFailed, isOnline } from "@/utils/offlineQueue";

class AuthService {
  constructor() {
    this.idleTimeout = 30 * 60 * 1000; // 30 minutes default
    this.warningTime = 5 * 60 * 1000; // Warning 5 minutes before logout
    this.idleTimer = null;
    this.warningTimer = null;
    this.warningNotification = null;
    this.lastActivity = Date.now();
    this.activityEvents = [
      "mousedown",
      "mousemove",
      "keypress",
      "scroll",
      "click",
    ];
    this.isListening = false;
    this.lastResetTime = 0;
    this.resetThrottle = 5000;
    this.refreshTimer = null;
    this.tokenRefreshInterval = 15 * 60 * 1000; // Refresh token every 15 minutes of activity
    this.lastTokenRefresh = 0;
  }

  init() {
    if (this.isListening) return;

    // Start listening for user activity with throttling
    this.handleActivity = this.handleActivity.bind(this);
    this.activityEvents.forEach((event) => {
      document.addEventListener(event, this.handleActivity, true);
    });

    this.isListening = true;
    this.lastTokenRefresh = Date.now(); // Initialize token refresh time
    this.resetIdleTimer();
  }

  handleActivity() {
    const now = Date.now();

    // Throttle the reset calls - only reset if enough time has passed
    if (now - this.lastResetTime >= this.resetThrottle) {
      this.resetIdleTimer();
      this.lastResetTime = now;

      // Check if we should refresh the token (every 15 minutes of activity)
      if (now - this.lastTokenRefresh >= this.tokenRefreshInterval) {
        this.refreshToken();
      }
    }
  }

  async refreshToken() {
    try {
      const token = localStorage.getItem("jwtToken");
      if (!token) return;

      const response = await fetch(
        `${process.env.VUE_APP_API_URL}/api/login/refresh-token`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.signUpToken) {
          localStorage.setItem("jwtToken", data.signUpToken);
          this.lastTokenRefresh = Date.now();
        }
      } else if (response.status === 401) {
        // Token is definitively invalid — remove it and hard-logout
        this.logout("Your session has expired. Please log in again.", { removeToken: true });
      }
    } catch (error) {
      // Token refresh failed silently
    }
  }

  destroy() {
    this.activityEvents.forEach((event) => {
      document.removeEventListener(event, this.handleActivity, true);
    });

    this.clearTimers();
    this.isListening = false;
  }

  resetIdleTimer() {
    this.lastActivity = Date.now();
    this.clearTimers();

    // Close any existing warning notification
    if (this.warningNotification) {
      this.warningNotification.close();
      this.warningNotification = null;
    }

    const token = localStorage.getItem("jwtToken");
    if (!token) return;

    this.warningTimer = setTimeout(() => {
      this.showWarning();
    }, this.idleTimeout - this.warningTime);

    this.idleTimer = setTimeout(() => {
      this.handleIdleTimeout();
    }, this.idleTimeout);
  }

  clearTimers() {
    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }
    if (this.warningTimer) {
      clearTimeout(this.warningTimer);
      this.warningTimer = null;
    }
  }

  showWarning() {
    this.warningNotification = Notification({
      title: "Session Expiring Soon",
      message:
        "Your session will expire in 5 minutes due to inactivity. Move your mouse or press any key to stay logged in.",
      type: "warning",
      duration: 300000,
      position: "bottom-right",
      showClose: true,
      dangerouslyUseHTMLString: false,
    });
  }

  handleIdleTimeout() {
    // Never idle-logout while offline or while offline work is still queued.
    // Logging out would strand the caregiver on a login screen they cannot
    // pass without a network — taking their queued clock-out with them.
    // Just re-arm the timer and check again next cycle.
    try {
      if (!isOnline() || getPending().length > 0 || getFailed().length > 0) {
        this.resetIdleTimer();
        return;
      }
    } catch (e) {
      // If the queue can't be read, fall through to the normal logout.
    }

    Notification({
      title: "Session Expired",
      message: "You have been logged out due to inactivity.",
      type: "error",
      duration: 5000,
      position: "bottom-right",
    });

    // removeToken intentionally omitted (defaults to false).
    // The token stays in localStorage so other active tabs are not disrupted.
    this.logout("Your session has expired due to inactivity.");
  }

  handleAuthError(error) {
    if (error?.response?.status === 401) {
      this.logout("Your session has expired. Please log in again.", { removeToken: true });
      return true;
    }
    return false;
  }

  logout(message = null, { removeToken = false } = {}) {
    const store = useLoginStore();

    // Only remove the shared token when it is definitively invalid.
    // Inactivity logout must NOT remove the token — other active tabs may still
    // be using it. Token removal should only happen when the backend explicitly
    // rejects it (401 on refresh) or on a deliberate user-initiated logout.
    if (removeToken) {
      localStorage.removeItem("jwtToken");
    }

    store.$reset();

    // destroy() resets isListening so init() will run fully on the next login
    this.destroy();

    // Navigate to login with message
    router.push({
      name: "Login",
      query: message ? { message } : {},
    });
  }

  // Check if token is expired (for JWT tokens)
  isTokenExpired() {
    const token = localStorage.getItem("jwtToken");
    if (!token) return true;

    try {
      // Parse JWT token
      const base64Url = token.split(".")[1];
      if (!base64Url) return true;

      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join(""),
      );

      const payload = JSON.parse(jsonPayload);

      // Check expiration
      if (payload.exp) {
        const expirationTime = payload.exp * 1000; // Convert to milliseconds
        return Date.now() >= expirationTime;
      }

      return false;
    } catch (error) {
      return true;
    }
  }

  // Set custom idle timeout (in minutes)
  setIdleTimeout(minutes) {
    this.idleTimeout = minutes * 60 * 1000;
    if (this.isListening) {
      this.resetIdleTimer();
    }
  }
}

export default new AuthService();
